import { json, jsonParseLinter } from '@codemirror/lang-json'
import { linter } from '@codemirror/lint'
import {
  Button,
  classes,
  CodeEditorV2,
  Heading,
  icons,
  stringify,
  useCopyToClipboard,
} from '@portal/components'
import React, { useMemo } from 'react'

import { useDQLEditorContext } from '../dqlEditor/context'

const { Check, Copy, ChevronDown, ChevronUp, X } = icons

// Drawer width constants
const DRAWER_WIDTH_MOBILE = 'w-56' // 224px
const DRAWER_WIDTH_DESKTOP = 'md:w-[500px]'

export function DocumentDetailDrawer() {
  // activeRow is automatically kept in sync with the latest data via DQLEditorProvider
  const { result, activeRow, setActiveRow } = useDQLEditorContext()

  // Index of the active row. -1 if no active row
  const activeRowIndex = useMemo(() => {
    return !activeRow
      ? -1
      : (result?.items?.findIndex((row) => row._id === activeRow._id) ?? -1)
  }, [result, activeRow])

  // Next and previous items are used to navigate between rows. Empty if there is no next/previous row
  const nextItem =
    activeRowIndex < (result?.items?.length ?? 0) - 1
      ? result?.items?.[activeRowIndex + 1]
      : null
  const previousItem =
    activeRowIndex > 0 ? result?.items?.[activeRowIndex - 1] : null

  const [performCopy, recentlyCopied, canCopy] = useCopyToClipboard(
    stringify(activeRow ?? {}, null, 2),
    2000,
  )

  const jsonLinter = linter(jsonParseLinter())

  const handleNextRow = () => {
    if (nextItem) {
      setActiveRow(nextItem)
    }
  }

  const handlePreviousRow = () => {
    if (previousItem) {
      setActiveRow(previousItem)
    }
  }

  return (
    <div
      role="dialog"
      aria-label="Document details"
      className={classes(
        'absolute bottom-0 right-0 top-0 z-50 h-full transform border-l border-solid border-border-normal bg-background-surface transition-transform duration-200 ease-in-out',
        DRAWER_WIDTH_MOBILE,
        DRAWER_WIDTH_DESKTOP,
        !!activeRow ? 'translate-x-0' : 'translate-x-full',
      )}
      data-testid="documentDetailDrawer"
    >
      <div className="flex h-full w-full flex-col">
        <header className="flex h-12 shrink-0 items-center justify-between border-b border-solid border-border-normal bg-background px-4">
          <Heading
            level={3}
            className="mb-0 inline-flex items-center gap-x-2 text-base sm:text-base"
          >
            Document
            {canCopy && (
              <Button
                aria-label="Copy document"
                onClick={performCopy}
                size="icon"
                className="border-none focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-focus-outline"
              >
                {recentlyCopied ? (
                  <Check className="size-4 text-fill-success" />
                ) : (
                  <Copy className="size-4 text-foreground-subtle text-opacity-100 hover:text-opacity-90" />
                )}
              </Button>
            )}
          </Heading>

          <div className="flex items-center gap-x-4">
            <Button
              aria-label="Previous row"
              onClick={handlePreviousRow}
              disabled={!previousItem}
              size="icon"
              className="border-none"
              data-testid="previousRowButton"
            >
              <ChevronUp className="h-5 w-5 text-foreground-subtle text-opacity-100 hover:text-opacity-90" />
            </Button>
            <Button
              aria-label="Next row"
              onClick={handleNextRow}
              disabled={!nextItem}
              size="icon"
              className="border-none"
              data-testid="nextRowButton"
            >
              <ChevronDown className="h-5 w-5 text-foreground-subtle text-opacity-100 hover:text-opacity-90" />
            </Button>
            <Button
              aria-label="Close document detail"
              onClick={() => setActiveRow(undefined)}
              size="icon"
              className="border-none"
              data-testid="closeButton"
            >
              <X className="h-5 w-5 text-foreground-subtle text-opacity-100 hover:text-opacity-90" />
            </Button>
          </div>
        </header>

        <div
          className="flex-1 overflow-y-auto bg-background-surface"
          aria-live="polite"
        >
          <CodeEditorV2
            basicSetup={{
              lineNumbers: true,
              highlightActiveLineGutter: false,
            }}
            value={stringify(activeRow ?? {}, null, 2)}
            language={json()}
            readOnly={true}
            linter={jsonLinter}
            className="h-full w-full"
          />
        </div>
      </div>
    </div>
  )
}
